import * as React from 'react';
import { GlobalPointerDownOutsideListener, PointerEventHandler } from './GlobalPointerDownOutsideListener';
import { ButtonAttrs, ElemAttrs, InputAttrs } from './HtmlAttrs';

export type FCProps<T> = T extends (arg1: infer U, ...args: unknown[]) => unknown ? U : undefined;

export type NavigationKey = ' ' | 'ArrowDown' | 'ArrowUp' | 'ArrowRight' | 'ArrowLeft' | 'End' | 'Enter' | 'Escape' | 'Home' | 'Tab' | 'PageUp' | 'PageDown';

// https://stackoverflow.com/questions/65577843/mutually-exclusive-props-in-a-react-component
export type MutuallyExclude<T, E extends keyof T = keyof T> =
  {
    [K in E]: { [P in K]: T[P] } & Omit<T, E> & {
      [P in Exclude<E, K>]?: never;
    } extends infer O
    ? { [P in keyof O]: O[P] }
    : never;
  }[E] | ({ [K in E]?: never } & Omit<T, E>);

export type RequireAria<K extends keyof React.AriaAttributes> = Required<Pick<React.AriaAttributes, K>>;
/** Requires that either `aria-label` or `aria-labelledby` is specified. */
export type RequireLabel = RequireAria<'aria-label'> | RequireAria<'aria-labelledby'>;

export type PropsAndElementAttrs<P, A extends ElemAttrs = ElemAttrs> = P & Omit<A, keyof P>;
export type PropsAndButtonAttrs<P> = PropsAndElementAttrs<P, ButtonAttrs>;
export type PropsAndInputAttrs<P> = PropsAndElementAttrs<P, InputAttrs>;

function isSFC<P>(type: React.ComponentType<P>): type is React.FunctionComponent<P> {
  return !type.prototype?.render;
}

export function convertToStatefulComponentClass<P>(componentType: React.ComponentType<P>): React.ComponentClass<P> {
  if (isSFC(componentType)) {
    const statelessComponent: React.FunctionComponent<P> = componentType;
    return class StatefulSFCWrapper extends React.Component<P> {
      public static displayName: string = getComponentName(statelessComponent);
      public render(): React.ReactNode {
        return statelessComponent(this.props);
      }
    };
  } else {
    return componentType;
  }
}

export function mergeEventHandlers<EType extends React.SyntheticEvent>(
  h1: React.EventHandler<EType>,
  h2?: React.EventHandler<EType>
): React.EventHandler<EType> {
  return (h1 && h2 && function (e: EType): void { h1(e); h2(e); })
    || h1 || h2;
}

function isCallbackRef<T>(ref?: React.Ref<T>): ref is ((instance: T | null) => void) {
  return typeof ref === 'function';
}

export const mergeRefs = <T>(...refs: Array<React.Ref<T> | undefined>) => (value: T | null): void => {
  refs.forEach(resolvableRef => assignRef(resolvableRef, value));
};

export function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (isCallbackRef(ref)) {
    ref(value);
  } else if (ref) {
    (ref as React.MutableRefObject<T>).current = value!;
  }
}

export function getComponentName(componentType: React.ComponentType): string {
  return componentType.displayName || componentType.name || 'AnonymousComponent';
}

/** Adding this prop tells VizClient's event-utils to completely ignore the element */
export const EventUtilsOptOutAttr: string = 'data-eventutils-optout';
export const EventUtilsOptOut = Object.freeze({
  [EventUtilsOptOutAttr]: true,
});

/** Adding this prop allows us to ignore certain "errors" flagged by the axe accessibility testing framework. TFSID 1193920 */
export const AxeOptOut = Object.freeze({
  'data-axe-ignore': true,
});

/** Marks certain properties of a type as explicitly-disallowed rather than just omitted. */
export type ProhibitPropsFrom<T, K extends keyof T> = Omit<T, K> & {
  [Property in keyof Pick<T, K>]: never
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type Prettify<T> = { [K in keyof T]: T[K] } & {};

/** Identical to React.useEffect, except that it never runs on mount. */
export const useEffectExceptOnMount: typeof React.useEffect = (effect, dependencies) => {
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (mounted.current) {
      return effect();
    } else {
      mounted.current = true;
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Reset on unmount for the next mount. (Relevant in React 18+)
  React.useEffect(() => {
    return () => { mounted.current = false; };
  }, []);
};

/**
 * Wrapper around React hook setup for using `ResizeObserver` to respond to resize of an element.
 * @param callback called upon resize of `element`
 * @param element `HTMLElement` to observe with the `ResizeObserver`
 * @param startObserving optional flag to control when observer should start and stop
 */
export const useResizeObserver = (element: HTMLElement | null, callback: ResizeObserverCallback, startObserving: boolean = true): void => {
  const resizeObserverRef = React.useRef<ResizeObserver | undefined>();

  const onResizeCallback = React.useCallback(callback, [callback]);

  const observe = React.useCallback(() => {
    if (element && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(onResizeCallback);
    }
    if (element) {
      resizeObserverRef.current?.observe(element);
    }
  }, [element, onResizeCallback]);

  const stopObserving = React.useCallback(() => {
    if (element && resizeObserverRef.current) {
      resizeObserverRef.current.unobserve(element);
      resizeObserverRef.current = undefined;
    }
  }, [element]);

  React.useEffect(() => {
    if (element && startObserving) {
      observe();
    }

    return stopObserving;
  }, [element, startObserving, observe, stopObserving]);
};

/**
 * Wrapper around React hook setup for using `GlobalPointerDownOutsideListener` `subscribe` and `unsubscribe`,
 * for e.g. to handle closing a popout element on click outside of the trigger elemnent.
 * @param triggerElement Element that triggers the popout
 * @param popoutElement Element popped out
 * @param handler triggered by pointer-down event
 */
export const useGlobalPointerDownOutsideListener = (triggerElement: HTMLElement, popoutElement: HTMLElement | null, handler: PointerEventHandler): void => {
  const handlerCallback = React.useCallback(handler, [handler]);
  React.useEffect(() => {
    if (popoutElement && triggerElement) {
      GlobalPointerDownOutsideListener.subscribe(triggerElement, handlerCallback);
    }
    return () => GlobalPointerDownOutsideListener.unsubscribe(triggerElement, handlerCallback);
  }, [popoutElement, triggerElement, handlerCallback]);
};

/** @returns if we're running in a Tableau Unified Analytics embed, i.e. if the `body` element has class `tua-embed` */
export function useTuaStatus(): boolean {
  const readTua = () => document.body.classList.contains('tua-embed');
  const [isTua, setTua] = React.useState(readTua())
  React.useEffect(() => {
    const observer = new MutationObserver(() => setTua(readTua()));
    observer.observe(document.body, { attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isTua;
}
